// Series configurations drive the comparison-chart picker. The picker tree
// is Activities-first: each Activity leaf carries the sub-filters that
// distinguish flavors of that activity (call outcomes, email subtypes,
// connection types). Old per-flavor kinds (meaningful_calls, personal_emails,
// campaign_emails) are retained in the union for backwards compatibility
// with saved views written before the restructure; migrateLegacySeries
// upgrades them on load.

export type SeriesKind =
  // Active picker leaves:
  | 'calls'
  | 'emails'
  | 'new_contacts'
  | 'new_connections'
  // Legacy kinds (saved views written before the restructure). Still
  // dispatched by the fetchers so old saved views render correctly.
  | 'meaningful_calls'
  | 'personal_emails'
  | 'campaign_emails';

export type EmailSubtype = 'personal' | 'campaign';

interface BaseSeries {
  id: string;
}

export type SeriesConfig =
  | (BaseSeries & { kind: 'calls'; outcomes?: string[] })
  | (BaseSeries & { kind: 'emails'; subtypes?: EmailSubtype[] })
  | (BaseSeries & { kind: 'new_contacts' })
  | (BaseSeries & { kind: 'new_connections'; relationships?: string[] })
  // Legacy:
  | (BaseSeries & { kind: 'meaningful_calls' })
  | (BaseSeries & { kind: 'personal_emails' })
  | (BaseSeries & { kind: 'campaign_emails' });

export const SERIES_GROUPS: ReadonlyArray<{
  group: string;
  items: ReadonlyArray<{ kind: SeriesKind; label: string }>;
}> = [
  {
    group: 'Activities',
    items: [
      { kind: 'calls', label: 'Calls' },
      { kind: 'emails', label: 'Email' },
    ],
  },
  {
    group: 'Contacts',
    items: [{ kind: 'new_contacts', label: 'New Contacts' }],
  },
  {
    group: 'Connections',
    items: [{ kind: 'new_connections', label: 'New Connections' }],
  },
];

export function seriesLabel(config: SeriesConfig): string {
  switch (config.kind) {
    case 'calls': {
      if (config.outcomes && config.outcomes.length > 0) {
        return `Calls — ${config.outcomes.join(', ')}`;
      }
      return 'Calls';
    }
    case 'emails': {
      const st = config.subtypes;
      if (!st || st.length === 0 || st.length === 2) return 'Email';
      return `Email — ${st[0] === 'personal' ? 'Personal' : 'Campaign'}`;
    }
    case 'new_contacts':
      return 'New Contacts';
    case 'new_connections': {
      if (config.relationships && config.relationships.length > 0) {
        return `New Connections — ${config.relationships.join(', ')}`;
      }
      return 'New Connections';
    }
    // Legacy:
    case 'meaningful_calls':
      return 'Meaningful Calls';
    case 'personal_emails':
      return 'Personal Emails';
    case 'campaign_emails':
      return 'Campaign Emails';
  }
}

// Default view: All Calls + Calls filtered to outcome=Meaningful Conversation.
// (The original spec was "Calls + Meaningful Calls overlaid", but the
// `meaningful` boolean is effectively never set in current data — the signal
// lives in the call_outcome value instead.)
export function defaultSeries(): SeriesConfig[] {
  return [
    { id: 's_calls', kind: 'calls' },
    { id: 's_meaningful', kind: 'calls', outcomes: ['Meaningful Conversation'] },
  ];
}

export function supportsOutcomeFilter(kind: SeriesKind): boolean {
  return kind === 'calls';
}

export function supportsRelationshipFilter(kind: SeriesKind): boolean {
  return kind === 'new_connections';
}

export function supportsEmailSubtypeFilter(kind: SeriesKind): boolean {
  return kind === 'emails';
}

export function newSeriesId(kind: SeriesKind): string {
  return `s_${kind}_${Math.random().toString(36).slice(2, 8)}`;
}

// Upgrade legacy SeriesConfig shapes to the active picker model.
// Used when loading saved views or applying the workspace_default that may
// have been written before the restructure.
export function migrateLegacySeries(config: SeriesConfig): SeriesConfig {
  switch (config.kind) {
    case 'meaningful_calls':
      return { id: config.id, kind: 'calls', outcomes: ['Meaningful Conversation'] };
    case 'personal_emails':
      return { id: config.id, kind: 'emails', subtypes: ['personal'] };
    case 'campaign_emails':
      return { id: config.id, kind: 'emails', subtypes: ['campaign'] };
    default:
      return config;
  }
}

export function migrateLegacySeriesList(list: SeriesConfig[]): SeriesConfig[] {
  return list.map(migrateLegacySeries);
}
